import React, { useEffect, useState } from "react";
import { collection, getDocs, query, doc, getDoc } from "firebase/firestore";
import { db } from "../utils/firebase";
import {
  LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip,
  ResponsiveContainer, BarChart, Bar
} from "recharts";

// --- SVG ICONS ---
const Icons = {
  Eye: (props) => <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>,
  EyeOff: (props) => <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>,
  WhatsApp: (props) => <svg {...props} viewBox="0 0 32 32" fill="currentColor"><path d="M16 3C9.4 3 4 8.4 4 15c0 2.5.8 4.8 2.2 6.8L4 29l7.4-2.2C13 28 14.5 28.5 16 28.5c6.6 0 12-5.4 12-12S22.6 3 16 3zM16 26c-1.4 0-2.7-.3-4-1l-.3-.2-4.3 1.3 1.3-4.1-.3-.4C7.7 20.1 7 17.6 7 15 7 9.5 11.5 5 17 5s10 4.5 10 10-4.5 11-11 11z" /><path d="M22.3 19.3c-.3-.2-1.8-.9-2.1-1-.3-.1-.5-.2-.7.2-.2.3-.8 1-1 1.2-.2.2-.4.2-.7.1-.3-.2-1.2-.5-2.3-1.5-1-.9-1.6-1.9-1.8-2.2-.2-.3 0-.5.1-.6.1-.1.3-.3.4-.5.1-.2.2-.3.3-.5.1-.2.1-.4 0-.5s-.6-1.5-.9-2.1c-.2-.6-.5-.5-.7-.6h-.6c-.2 0-.5.1-.8.3-.2.2-.9.9-.9 2.1s.9 2.4 1 2.5c.1.2 1.9 3 4.6 4.2 2.7 1.2 2.7.8 3.2.7.5-.1 1.4-.6 1.6-1.1.2-.5.2-1 .1-1.2-.1-.2-.3-.2-.6-.4z" /></svg>,
  Calendar: (props) => <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>,
  Close: (props) => <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
};

const defaultTemplates = [
  "👋 Hi {name}, hope you're doing great! 🎉 We’ve got exciting *new offers & discounts* just for you. Use code *WELCOME10* and enjoy *10% OFF* on your next order. Don’t miss out! 🍽️",
  "🙏 Hi {name}, thank you for choosing us! 💚 Could you take a moment to *share your feedback*? Your opinion helps us improve and serve you better. Tap here to review: [Insert Link] ⭐",
  "🍲 Hi {name}, guess what? We’ve just added a *delicious new dish* to our menu! 🤩 Come try it today and mention this message to get a *complimentary side*. See you soon! 🥗",
  "🌟 Hi {name}, thanks for being a *loyal customer*! As a token of appreciation, show this message on your next visit to get a *special loyalty discount*. 🎁 We can’t wait to serve you again!"
];

export default function AnalyticsPage() {
  // --- STATE ---
  const getCurrentMonthRange = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { start, end };
  };
  const { start, end } = getCurrentMonthRange();
  const [templates, setTemplates] = useState(defaultTemplates);

  const [analyticsData, setAnalyticsData] = useState([]);
  const [fromDate, setFromDate] = useState(start.toISOString().split("T")[0]);
  const [toDate, setToDate] = useState(end.toISOString().split("T")[0]);
  const [loading, setLoading] = useState(false);

  const [topCustomers, setTopCustomers] = useState([]);
  const [topItemsV2, setTopItemsV2] = useState([]);
  const [customersTable, setCustomersTable] = useState([]);

  // Table & Modal State
  const [currentPage, setCurrentPage] = useState(1);
  const ROWS_PER_PAGE = 15;
  const [unmaskedRows, setUnmaskedRows] = useState({});
  const [whatsappModalOpen, setWhatsappModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [messageText, setMessageText] = useState("");

  const restaurantEmail = localStorage.getItem("user");

  const [sortField, setSortField] = useState("orders");
  const [sortDirection, setSortDirection] = useState("desc");

  useEffect(() => {

    const loadTemplates = async () => {

      if (!restaurantEmail) return;

      try {

        const ref = doc(db, "whatsappTemplates", restaurantEmail);
        const snap = await getDoc(ref);

        if (snap.exists()) {
          const dbTemplates = snap.data()?.templates;

          if (Array.isArray(dbTemplates) && dbTemplates.length > 0) {
            setTemplates(dbTemplates.slice(0, 4));
          } else {
            setTemplates(defaultTemplates);
          }

        } else {
          setTemplates(defaultTemplates);
        }

      } catch (err) {
        console.error("Template load error:", err);
        setTemplates(defaultTemplates);
      }

    };

    loadTemplates();

  }, [restaurantEmail]);


  // --- LOGIC ---
  function getWeekStartDate(date = new Date()) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff)).toISOString().slice(0, 10);
  }

  const getLastVisitedDays = (timestamp) => {
    if (!timestamp) return "-";

    const last = timestamp.toDate();
    const today = new Date();

    const diff = Math.floor((today - last) / (1000 * 60 * 60 * 24));

    return diff;
  };

  const getCustomerStatus = (days) => {
    if (days === "-" || days === null || days === undefined) return { label: "-", color: "" };

    if (days <= 30) {
      return { label: "Active", color: "green" };
    }

    if (days <= 60) {
      return { label: "Warm", color: "yellow" };
    }

    return { label: "At Risk", color: "red" };
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const fetchAnalyticsV2 = async () => {
    if (loading) return;
    if (!restaurantEmail) return;

    if (new Date(fromDate) > new Date(toDate)) return;

    setLoading(true);

    const start = new Date(fromDate);
    const end = new Date(toDate);
    const weekSet = new Set();
    const d = new Date(start);

    while (d <= end) {
      weekSet.add(getWeekStartDate(d));
      d.setDate(d.getDate() + 7);
    }

    const customersAgg = {};
    const itemsAgg = {};

    const weekPromises = Array.from(weekSet).map(async (week) => {
      let weeklyOrders = 0;
      let weeklyRevenue = 0;

      const totalsRef = doc(
        db,
        "analytics_v2",
        restaurantEmail,
        "weekly",
        week,
        "totals",
        "summary"
      );

      const customersRef = collection(
        db,
        "analytics_v2",
        restaurantEmail,
        "weekly",
        week,
        "customers"
      );

      const itemsRef = collection(
        db,
        "analytics_v2",
        restaurantEmail,
        "weekly",
        week,
        "items"
      );

      const [totalsSnap, custSnap, itemSnap] = await Promise.all([
        getDoc(totalsRef),
        getDocs(customersRef),
        getDocs(itemsRef),
      ]);

      if (totalsSnap.exists()) {
        const t = totalsSnap.data();
        weeklyOrders = t.totalOrders || 0;
        weeklyRevenue = t.totalRevenue || 0;
      }

      custSnap.forEach((s) => {
        const c = s.data();

        if (!customersAgg[c.phone]) {
          customersAgg[c.phone] = { ...c };
        } else {
          customersAgg[c.phone].ordersCount += c.ordersCount;
          customersAgg[c.phone].totalSpent += c.totalSpent;

          // keep the latest order timestamp
          if (
            c.lastOrderAt &&
            customersAgg[c.phone].lastOrderAt &&
            c.lastOrderAt.seconds > customersAgg[c.phone].lastOrderAt.seconds
          ) {
            customersAgg[c.phone].lastOrderAt = c.lastOrderAt;
          }
        }
      });

      itemSnap.forEach((s) => {
        const i = s.data();
        if (!itemsAgg[i.itemName]) {
          itemsAgg[i.itemName] = { ...i };
        } else {
          itemsAgg[i.itemName].qty += i.qty;
          itemsAgg[i.itemName].revenue += i.revenue;
        }
      });

      return {
        date: week,
        totalOrders: weeklyOrders,
        totalRevenue: weeklyRevenue,
      };
    });

    const timelineData = await Promise.all(weekPromises);

    setAnalyticsData(timelineData.sort((a, b) => a.date.localeCompare(b.date)));

    setTopCustomers(
      Object.values(customersAgg)
        .sort((a, b) => b.totalSpent - a.totalSpent)
        .slice(0, 10)
    );

    setCustomersTable(
      Object.values(customersAgg).sort(
        (a, b) => b.ordersCount - a.ordersCount
      )
    );

    setTopItemsV2(
      Object.values(itemsAgg)
        .sort((a, b) => b.qty - a.qty)
        .slice(0, 10)
    );

    setCurrentPage(1);
    setLoading(false);
  };


  useEffect(() => {
    fetchAnalyticsV2();
  }, []);

  // --- CALCULATIONS ---
  const totalOrders = analyticsData.reduce((sum, d) => sum + (d.totalOrders || 0), 0);
  const totalRevenue = analyticsData.reduce((sum, d) => sum + (d.totalRevenue || 0), 0);
  const uniqueCustomers = customersTable.length;
  const repeatCustomers = customersTable.filter(c => c.ordersCount > 1).length;

  const getDayDiff = (from, to) => Math.ceil((new Date(to) - new Date(from)) / (1000 * 60 * 60 * 24));
  const rangeDays = getDayDiff(fromDate, toDate);

  let chartData = analyticsData.map(d => ({
    date: rangeDays > 45 ? (d.weekStart || d.date || d.id) : (d.date || d.weekStart || d.id),
    orders: d.totalOrders || 0,
    revenue: d.totalRevenue || 0
  })).sort((a, b) => a.date.localeCompare(b.date));

  const topCustomersBySpend = topCustomers.map(c => ({ name: c.name, phone: c.phone, spent: c.totalSpent }));
  const topCustomersByOrders = customersTable.slice(0, 10).map(c => ({ name: c.name, phone: c.phone, orders: c.ordersCount }));
  const customersArray = customersTable.map(c => {
    const lastVisited = getLastVisitedDays(c.lastOrderAt);
    const status = getCustomerStatus(lastVisited);

    return {
      phone: c.phone,
      name: c.name,
      spent: c.totalSpent,
      orders: c.ordersCount,
      lastVisited,
      status
    };
  });

  const totalPages = Math.max(1, Math.ceil(customersArray.length / ROWS_PER_PAGE));
  const sortedCustomers = [...customersArray].sort((a, b) => {
    let valA = a[sortField];
    let valB = b[sortField];

    if (sortField === "name") {
      valA = valA?.toLowerCase();
      valB = valB?.toLowerCase();
    }

    if (valA < valB) return sortDirection === "asc" ? -1 : 1;
    if (valA > valB) return sortDirection === "asc" ? 1 : -1;

    return 0;
  });

  const pagedCustomers = sortedCustomers.slice(
    (currentPage - 1) * ROWS_PER_PAGE,
    currentPage * ROWS_PER_PAGE
  );

  // --- HELPERS ---
  const maskPhone = (phone) => {
    if (!phone) return "";
    const s = String(phone);
    return s.length <= 2 ? "**" : `${"*".repeat(s.length - 2)}${s.slice(-2)}`;
  };
  const toggleUnmask = (phone) => setUnmaskedRows(prev => ({ ...prev, [phone]: !prev[phone] }));
  const goToPage = (p) => {
    if (p >= 1 && p <= totalPages) setCurrentPage(p);
  };

  const openWhatsappModal = (customer) => {
    setSelectedCustomer(customer);
    setMessageText(`Hi ${customer.name}, `);
    setWhatsappModalOpen(true);
  };
  const closeWhatsappModal = () => {
    setWhatsappModalOpen(false);
    setSelectedCustomer(null);
    setMessageText("");
  };
  const applyTemplate = (template) => {
    if (selectedCustomer) setMessageText(template.replace(/{name}/g, selectedCustomer.name));
  };
  const sendWhatsapp = () => {
    if (!selectedCustomer) return;
    const phone = selectedCustomer.phone.replace(/\D/g, '');
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(messageText)}`, "_blank");
    closeWhatsappModal();
  };

  // --- UI COMPONENTS ---
  const Skeleton = ({ className }) => <div className={`animate-pulse bg-slate-200 rounded-md ${className}`} />;
  const ChartWrapper = ({ title, children }) => (
    <div className="bg-white border border-slate-100 shadow-sm rounded-xl p-6 flex flex-col">
      <h3 className="text-slate-600 font-semibold mb-6 text-sm uppercase tracking-wider">{title}</h3>
      {loading ? <Skeleton className="flex-1 min-h-[250px] w-full" /> : <ResponsiveContainer width="100%" height={260}>{children}</ResponsiveContainer>}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 p-6 md:p-10 font-sans text-slate-800">

      {/* 1. Header & Controls */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Analytics Dashboard</h1>
          <p className="text-slate-500 mt-1">Track your business performance and customer engagement.</p>
        </div>

        <div className="flex flex-wrap items-end gap-3 bg-white p-2 rounded-xl border border-slate-200 shadow-sm">
          <div className="flex flex-col px-2">
            <label className="text-xs font-semibold text-slate-500 mb-1 uppercase">From</label>
            <div className="relative">
              <input type="date" value={fromDate} max={toDate} onChange={(e) => setFromDate(e.target.value)} className="pl-2 pr-8 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#6cbb23]/50" />
            </div>
          </div>
          <div className="flex flex-col px-2">
            <label className="text-xs font-semibold text-slate-500 mb-1 uppercase">To</label>
            <input type="date" value={toDate} min={fromDate} max={new Date().toISOString().split("T")[0]} onChange={(e) => setToDate(e.target.value)} className="pl-2 pr-8 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#6cbb23]/50" />
          </div>
          <button onClick={fetchAnalyticsV2} disabled={loading} className="bg-[#6cbb23] hover:bg-[#5da01e] text-white px-5 py-2 rounded-lg font-medium transition-colors shadow-sm disabled:opacity-70 flex items-center gap-2">
            {loading ? <span className="animate-spin text-xl leading-none">⟳</span> : <Icons.Calendar className="w-4 h-4" />}
            {loading ? "Updating..." : "Apply Filter"}
          </button>
        </div>
      </div>

      {/* 2. KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {[
          { label: "Total Orders", value: totalOrders, prefix: "" },
          { label: "Total Revenue", value: totalRevenue, prefix: "₹" }, // Change currency symbol if needed
          { label: "Unique Customers", value: uniqueCustomers, prefix: "" },
          { label: "Repeat Customers", value: repeatCustomers, prefix: "" },
        ].map((kpi, i) => (
          <div key={i} className="bg-white border border-slate-100 shadow-sm rounded-xl p-6 relative overflow-hidden group hover:shadow-md transition-shadow">
            <div className="absolute top-0 left-0 w-1 h-full bg-[#6cbb23] opacity-50 group-hover:opacity-100 transition-opacity" />
            <h2 className="text-slate-500 text-sm font-semibold uppercase tracking-wide">{kpi.label}</h2>
            <div className="mt-3 flex items-baseline gap-1">
              {loading ? <Skeleton className="h-10 w-24" /> : (
                <>
                  <span className="text-2xl font-medium text-slate-400">{kpi.prefix}</span>
                  <span className="text-4xl font-extrabold text-slate-800">{kpi.value.toLocaleString()}</span>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* 3. Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <ChartWrapper title="Orders Trend">
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
            <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
            <Line type="monotone" dataKey="orders" stroke="#6cbb23" strokeWidth={3} dot={{ r: 0 }} activeDot={{ r: 6, fill: "#6cbb23", stroke: "#fff", strokeWidth: 2 }} />
          </LineChart>
        </ChartWrapper>

        <ChartWrapper title="Revenue Trend">
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
            <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
            <Line type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={3} dot={{ r: 0 }} activeDot={{ r: 6, fill: "#3b82f6", stroke: "#fff", strokeWidth: 2 }} />
          </LineChart>
        </ChartWrapper>

        <ChartWrapper title="Top Customers (By Spend)">
          <BarChart data={topCustomersBySpend} margin={{ bottom: 40 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
            <XAxis dataKey="name" angle={-45} textAnchor="end" interval={0} tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} />
            <Tooltip cursor={{ fill: '#f8fafc' }} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
            <Bar dataKey="spent" fill="#f97316" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ChartWrapper>

        <ChartWrapper title="Top Items (By Quantity)">
          <BarChart data={topItemsV2} margin={{ bottom: 40 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
            <XAxis dataKey="itemName" angle={-45} textAnchor="end" interval={0} tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} />
            <Tooltip cursor={{ fill: '#f8fafc' }} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
            <Bar dataKey="qty" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ChartWrapper>
      </div>

      {/* 4. Full Width Customers Table */}
      <div className="bg-white border border-slate-100 shadow-sm rounded-xl overflow-hidden">
        <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
          <div>
            <h2 className="text-lg font-bold text-slate-800">Customer Directory & Campaigns</h2>
            <p className="text-sm text-slate-500">View customer stats and send promotional WhatsApp messages.</p>
          </div>
          <div className="text-sm font-medium text-slate-500 bg-white px-3 py-1 rounded-full border border-slate-200">
            {customersArray.length} Total Users
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-slate-600">
            <thead className="bg-slate-50 text-slate-500 uppercase text-xs font-semibold">
              <tr>
                <th className="px-6 py-4 border-b border-slate-100">Customer Name</th>
                <th className="px-6 py-4 border-b border-slate-100">Mobile Number</th>
                <th
                  onClick={() => handleSort("orders")}
                  className="px-6 py-4 border-b border-slate-100 text-center cursor-pointer hover:text-[#6cbb23]"
                >
                  Total Orders ▲▼
                </th>
                <th
                  onClick={() => handleSort("spent")}
                  className="px-6 py-4 border-b border-slate-100 text-center cursor-pointer hover:text-[#6cbb23]"
                >
                  Total Spent ▲▼
                </th>
                <th
                  onClick={() => handleSort("lastVisited")}
                  className="px-6 py-4 border-b border-slate-100 text-center cursor-pointer hover:text-[#6cbb23]"
                >
                  Last Visit ▲▼
                </th>
                <th className="px-6 py-4 border-b border-slate-100 text-center">Status</th>
                <th className="px-6 py-4 border-b border-slate-100 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pagedCustomers.length === 0 && (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-slate-400">
                    No customers found for this date range.
                  </td>
                </tr>
              )}
              {pagedCustomers.map((c) => (
                <tr key={c.phone} className="hover:bg-slate-50 transition-colors group">
                  <td className="px-6 py-4 font-medium text-slate-800">{c.name}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-slate-600 bg-slate-100 px-2 py-1 rounded">
                        {unmaskedRows[c.phone] ? c.phone : maskPhone(c.phone)}
                      </span>
                      <button onClick={() => toggleUnmask(c.phone)} className="text-slate-400 hover:text-[#6cbb23] transition-colors p-1" title="Toggle visibility">
                        {unmaskedRows[c.phone] ? <Icons.EyeOff className="w-4 h-4" /> : <Icons.Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="inline-flex items-center justify-center bg-blue-50 text-blue-700 font-medium px-2.5 py-0.5 rounded-full">
                      {c.orders}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center font-medium">₹{c.spent.toLocaleString()}</td>
                  <td className="px-6 py-4 text-center">
                    <span className="inline-flex items-center justify-center bg-slate-100 text-slate-700 font-medium px-2.5 py-0.5 rounded-full">
                      {c.lastVisited} days
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-semibold
    ${c.status.color === "green" ? "bg-green-100 text-green-700" : ""}
    ${c.status.color === "yellow" ? "bg-yellow-100 text-yellow-700" : ""}
    ${c.status.color === "red" ? "bg-red-100 text-red-700" : ""}
  `}>
                      {c.status.color === "green" && "🟢"}
                      {c.status.color === "yellow" && "🟡"}
                      {c.status.color === "red" && "🔴"}
                      {" "}
                      {c.status.label}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button onClick={() => openWhatsappModal({ name: c.name, phone: c.phone })} className="inline-flex items-center gap-2 bg-[#25D366]/10 text-[#25D366] hover:bg-[#25D366] hover:text-white px-4 py-2 rounded-lg font-medium transition-colors">
                      <Icons.WhatsApp className="w-5 h-5" />
                      <span>Message</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="p-4 border-t border-slate-100 flex items-center justify-between bg-slate-50/50">
          <span className="text-sm text-slate-500">
            Showing <span className="font-medium text-slate-700">{pagedCustomers.length > 0 ? (currentPage - 1) * ROWS_PER_PAGE + 1 : 0}</span> to <span className="font-medium text-slate-700">{Math.min(currentPage * ROWS_PER_PAGE, customersArray.length)}</span> of <span className="font-medium text-slate-700">{customersArray.length}</span> entries
          </span>
          <div className="flex items-center gap-1">
            <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1.5 border border-slate-200 text-slate-600 rounded-md hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium">Previous</button>
            <div className="px-4 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-md">
              {currentPage} / {totalPages || 1}
            </div>
            <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0} className="px-3 py-1.5 border border-slate-200 text-slate-600 rounded-md hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium">Next</button>
          </div>
        </div>
      </div>

      {/* 5. WhatsApp Modal Overlay */}
      {whatsappModalOpen && selectedCustomer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden flex flex-col md:flex-row animate-in fade-in zoom-in-95 duration-200">

            {/* Left Sidebar: Templates */}
            <div className="md:w-1/3 bg-slate-50 border-r border-slate-200 p-6 flex flex-col max-h-[80vh] overflow-y-auto">
              <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                <span className="bg-yellow-100 p-1.5 rounded-md text-yellow-600">⚡</span> Quick Templates
              </h3>
              <div className="space-y-3">
                {templates.map((t, i) => (
                  <button key={i} onClick={() => applyTemplate(t)} className="w-full text-left p-4 rounded-xl bg-white border border-slate-200 hover:border-[#6cbb23] hover:shadow-md transition-all text-sm text-slate-600 leading-relaxed group">
                    <span className="line-clamp-3 group-hover:text-slate-800 transition-colors">
                      {t.replace(/{name}/g, selectedCustomer.name)}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Right Side: Message Editor */}
            <div className="flex-1 p-6 flex flex-col relative max-h-[80vh]">
              <button onClick={closeWhatsappModal} className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 bg-slate-50 hover:bg-slate-100 rounded-full transition-colors">
                <Icons.Close className="w-5 h-5" />
              </button>

              <div className="mb-6 mt-2">
                <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Sending message to</p>
                <div className="flex items-center gap-3 mt-2">
                  <div className="w-10 h-10 bg-[#6cbb23]/10 rounded-full flex items-center justify-center text-[#6cbb23] font-bold text-lg">
                    {selectedCustomer.name.charAt(0)}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-800">{selectedCustomer.name}</h2>
                    <p className="text-sm text-slate-500 font-mono">{selectedCustomer.phone}</p>
                  </div>
                </div>
              </div>

              <div className="flex-1 flex flex-col">
                <label className="block text-sm font-semibold text-slate-700 mb-2">Message Content</label>
                <textarea
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  className="flex-1 w-full border border-slate-200 rounded-xl p-4 text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:border-transparent resize-none leading-relaxed"
                  placeholder="Type your message here..."
                />
              </div>

              <div className="mt-6 flex justify-end gap-3 pt-4 border-t border-slate-100">
                <button onClick={closeWhatsappModal} className="px-6 py-2.5 rounded-xl font-medium text-slate-600 hover:bg-slate-100 transition-colors">
                  Cancel
                </button>
                <button onClick={sendWhatsapp} className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[#25D366] text-white font-medium hover:bg-[#1ebd5a] hover:shadow-lg hover:shadow-[#25D366]/20 transition-all active:scale-95">
                  <Icons.WhatsApp className="w-5 h-5" />
                  Send via WhatsApp
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}